import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { CustomerService } from '../../../../services/customer.service';
import { Customer } from '../../../../models/customer.model';
import { ExpenseTypeService } from '../../../../services/expense-type.service';
import { ExpenseType } from '../../../../models/expense-type.model';
import { BankAccountService } from '../../../../services/bankAccount.service';
import { BankAccount } from '../../../../models/bankAccount.model';
import { FinancialService } from '../../../../services/financial.service';
import { CreateExpenseDto } from '../../../../models/dtos/createExpense.dto';
import { TypePaymentService } from '../../../../services/typePayment.service';
import { TypePayment } from '../../../../models/typePayment.model';
import { CompanyService } from '../../../../services/company.service';
import { Company } from '../../../../models/company.model';
import { SupplierService } from '../../../../services/supplier.service';
import { Supplier } from '../../../../models/supplier.model';
import { EmployeeService } from '../../../../services/employee.service';
import { Employee } from '../../../../models/employee.model';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { addMonths } from 'date-fns';

@Component({
  selector: 'app-form-expense',
  standalone: true,
  templateUrl: './form-expense.component.html',
  imports: [
    CommonModule, 
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,DropdownModule, CalendarModule, InputNumberModule, CheckboxModule, ButtonModule,CardModule,TextareaModule,
    DialogModule,
    TableModule,
  ],
  providers: [CustomerService, ExpenseTypeService, BankAccountService, FinancialService, TypePaymentService, CompanyService, SupplierService, EmployeeService]
})
export class FormExpenseComponent implements OnInit {
  form!: FormGroup;
  isEdit = false;
  customers: Customer[] = [];
  companies: Company[] = [];
  suppliers: Supplier[] = [];
  employees: Employee[] = [];
  customerDropdownOptions: any[] = [];
  customerLabel: string = 'Funcionário';
  expenseTypes: ExpenseType[] = [];
  expenseTypeDropdownOptions: any[] = [];
  bankAccounts: BankAccount[] = [];
  typePayments: TypePayment[] = [];
  typePaymentDropdownOptions: any[] = [];

  // Select options
  occurrenceOptions = ['Única', 'Parcelada/Recorrente'];
  expenseTypeOptions = [
    { label: 'Fornecedor', value: 'Supplier' },
    { label: 'Funcionário', value: 'Employee' },
    { label: 'Empresa', value: 'Company' }
  ];
  installmentIntervalOptions = [
    { label: 'Quinzenal', value: 'quinzenal' },
    { label: 'Mensal', value: 'mensal' },
    { label: 'Semestral', value: 'semestral' },
    { label: 'Anual', value: 'anual' },
    { label: 'Personalizado (dias)', value: 'custom' }
  ];
  installmentStatusOptions = [
    { label: 'Quitado', value: 'quitado' },
    { label: 'Lançamento Futuro', value: 'futuro' }
  ];
  installments: any[] = [];

  showParcelModal = false;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
    private expenseTypeService: ExpenseTypeService,
    private bankAccountService: BankAccountService,
    private financialService: FinancialService,
    private typePaymentService: TypePaymentService,
    private companyService: CompanyService,
    private supplierService: SupplierService,
    private employeeService: EmployeeService
  ) {}

  ngOnInit(): void {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
    this.form = this.fb.group({
      isPaid: [false, Validators.required],
      description: ['', Validators.required],
      customer: ['', Validators.required],
      expenseType: ['', Validators.required],
      expenseTypeCategory: ['Employee', Validators.required],
      occurrence: ['Única', Validators.required],
      amount: [null, [Validators.required, Validators.min(0.01)]],
      dueDate: [nextMonth, Validators.required],
      bankAccount: ['', Validators.required],
      paymentMethod: ['', Validators.required],
      interest: [null, [Validators.required, Validators.min(0)]],
      fine: [null, [Validators.required, Validators.min(0)]],
      barcode: ['', Validators.required],
      notes: [''],
      // Novos campos para recorrência/parcelamento
      installmentInterval: ['mensal'],
      customIntervalDays: [{ value: '', disabled: true }],
      installmentCount: [2],
      firstInstallmentDate: ['']
    });

    // Inicializar arrays de dropdown
    this.customerDropdownOptions = [{ label: 'Selecione um cliente', value: '' }];
    this.expenseTypeDropdownOptions = [{ label: 'Selecione um tipo de despesa', value: '' }];
    this.typePaymentDropdownOptions = [{ label: 'Selecione um método de pagamento', value: '' }];

    this.loadExpenseTypes();
    this.loadBankAccounts();
    this.loadTypePayments();
    this.loadEntitiesByCategory('Employee'); // Carrega funcionários por padrão

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEdit = true;
    }

    this.form.get('occurrence')?.valueChanges.subscribe(val => {
      if (val === 'Parcelada/Recorrente') {
        this.form.get('installmentInterval')?.setValidators([Validators.required]);
        this.form.get('installmentCount')?.setValidators([Validators.required, Validators.min(2)]);
        this.form.get('firstInstallmentDate')?.setValidators([Validators.required]);
      } else {
        this.form.get('installmentInterval')?.clearValidators();
        this.form.get('installmentCount')?.clearValidators();
        this.form.get('firstInstallmentDate')?.clearValidators();
        this.installments = [];
      }
      this.form.get('installmentInterval')?.updateValueAndValidity();
      this.form.get('installmentCount')?.updateValueAndValidity();
      this.form.get('firstInstallmentDate')?.updateValueAndValidity();
    });
    this.form.get('installmentInterval')?.valueChanges.subscribe(val => {
      if (val === 'custom') {
        this.form.get('customIntervalDays')?.enable();
      } else {
        this.form.get('customIntervalDays')?.disable();
        this.form.get('customIntervalDays')?.setValue('');
      }
      this.generateInstallments();
    });
    this.form.get('installmentCount')?.valueChanges.subscribe(() => this.generateInstallments());
    this.form.get('firstInstallmentDate')?.valueChanges.subscribe(() => this.generateInstallments());
    this.form.get('amount')?.valueChanges.subscribe(() => this.generateInstallments());
    this.form.get('customIntervalDays')?.valueChanges.subscribe(() => this.generateInstallments());

    // Listener para mudanças na categoria da despesa
    this.form.get('expenseTypeCategory')?.valueChanges.subscribe(category => {
      if (category) {
        this.loadEntitiesByCategory(category);
        this.form.get('customer')?.setValue(''); // Limpa o cliente selecionado
      }
    });
  }

  generateInstallments() {
    if (this.form.get('occurrence')?.value !== 'Parcelada/Recorrente') {
      this.installments = [];
      return;
    }
    const count = Number(this.form.get('installmentCount')?.value);
    const firstDate = this.form.get('firstInstallmentDate')?.value;
    const total = Number(this.form.get('amount')?.value);
    const interval = this.form.get('installmentInterval')?.value;
    const customDays = Number(this.form.get('customIntervalDays')?.value);
    if (!count || !firstDate || !total || !interval || (interval === 'custom' && !customDays)) {
      this.installments = [];
      return;
    }
    const valuePerInstallment = +(total / count).toFixed(2);
    let currentDate = new Date(firstDate);
    this.installments = [];
    for (let i = 0; i < count; i++) {
      this.installments.push({
        date: new Date(currentDate),
        amount: valuePerInstallment,
        status: 'futuro',
        paymentMethod: this.form.get('paymentMethod')?.value || ''
      });
      switch (interval) {
        case 'quinzenal':
          currentDate.setDate(currentDate.getDate() + 15);
          break;
        case 'mensal':
          currentDate.setMonth(currentDate.getMonth() + 1);
          break;
        case 'semestral':
          currentDate.setMonth(currentDate.getMonth() + 6);
          break;
        case 'anual':
          currentDate.setFullYear(currentDate.getFullYear() + 1);
          break;
        case 'custom':
          currentDate.setDate(currentDate.getDate() + customDays);
          break;
      }
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  loadTypePayments(): void {
    this.typePaymentService.getAll().subscribe({
      next: (response: any) => {
        if (response && response.data) {
          this.typePayments = response.data;
          this.typePaymentDropdownOptions = [
            { label: 'Selecione um método de pagamento', value: '' },
            ...this.typePayments.map(tp => ({ label: tp.description, value: tp.id }))
          ];
        } else {
          console.warn('Resposta da API de tipos de pagamento não tem a estrutura esperada:', response);
          this.typePayments = [];
          this.typePaymentDropdownOptions = [
            { label: 'Selecione um método de pagamento', value: '' }
          ];
        }
      },
      error: (error: any) => {
        console.error('Erro ao carregar tipos de pagamento:', error);
        this.typePayments = [];
        this.typePaymentDropdownOptions = [
          { label: 'Selecione um método de pagamento', value: '' }
        ];
      }
    });
  }

  loadEntitiesByCategory(category: string): void {
    switch (category) {
      case 'Company':
        this.customerLabel = 'Empresa';
        this.loadCompanies();
        break;
      case 'Supplier':
        this.customerLabel = 'Fornecedor';
        this.loadSuppliers();
        break;
      case 'Employee':
        this.customerLabel = 'Funcionário';
        this.loadEmployees();
        break;
      default:
        this.customerLabel = 'Entidade';
        this.customerDropdownOptions = [{ label: 'Selecione uma categoria primeiro', value: '' }];
    }
  }

  loadCompanies(): void {
    this.companyService.getAll().subscribe({
      next: (response: any) => {
        if (response && response.data) {
          this.companies = response.data;
          this.customerDropdownOptions = [
            { label: 'Selecione uma empresa', value: '' },
            ...this.companies.map(c => ({ label: c.razaoSocial, value: c.id }))
          ];
        } else {
          console.warn('Resposta da API de empresas não tem a estrutura esperada:', response);
          this.customerDropdownOptions = [{ label: 'Selecione uma empresa', value: '' }];
        }
      },
      error: (error: any) => {
        console.error('Erro ao carregar empresas:', error);
        this.customerDropdownOptions = [{ label: 'Selecione uma empresa', value: '' }];
      }
    });
  }

  loadSuppliers(): void {
    this.supplierService.getAll().subscribe({
      next: (response: any) => {
        if (response && response.data) {
          this.suppliers = response.data;
          this.customerDropdownOptions = [
            { label: 'Selecione um fornecedor', value: '' },
            ...this.suppliers.map(s => ({ label: s.companyName, value: s.id }))
          ];
        } else {
          console.warn('Resposta da API de fornecedores não tem a estrutura esperada:', response);
          this.customerDropdownOptions = [{ label: 'Selecione um fornecedor', value: '' }];
        }
      },
      error: (error: any) => {
        console.error('Erro ao carregar fornecedores:', error);
        this.customerDropdownOptions = [{ label: 'Selecione um fornecedor', value: '' }];
      }
    });
  }

  loadEmployees(): void {
    this.employeeService.getAll().subscribe({
      next: (response: any) => {
        if (response && response.data) {
          this.employees = response.data;
          this.customerDropdownOptions = [
            { label: 'Selecione um funcionário', value: '' },
            ...this.employees.map(e => ({ label: e.fullName, value: e.id }))
          ];
        } else {
          console.warn('Resposta da API de funcionários não tem a estrutura esperada:', response);
          this.customerDropdownOptions = [{ label: 'Selecione um funcionário', value: '' }];
        }
      },
      error: (error: any) => {
        console.error('Erro ao carregar funcionários:', error);
        this.customerDropdownOptions = [{ label: 'Selecione um funcionário', value: '' }];
      }
    });
  }

  loadExpenseTypes(): void {
    this.expenseTypeService.getAll().subscribe({
      next: (response: any) => {
        if (response && response.data) {
          this.expenseTypes = response.data;
          this.expenseTypeDropdownOptions = [
            { label: 'Selecione um tipo de despesa', value: '' },
            ...this.expenseTypes.map(et => ({ label: et.description, value: et.id }))
          ];
        } else {
          console.warn('Resposta da API de tipos de despesa não tem a estrutura esperada:', response);
          this.expenseTypes = [];
          this.expenseTypeDropdownOptions = [
            { label: 'Selecione um tipo de despesa', value: '' }
          ];
        }
      },
      error: (error: any) => {
        console.error('Erro ao carregar tipos de despesa:', error);
        this.expenseTypes = [];
        this.expenseTypeDropdownOptions = [
          { label: 'Selecione um tipo de despesa', value: '' }
        ];
      }
    });
  }

  loadBankAccounts(): void {
    this.bankAccountService.list('', true, 1).subscribe({
      next: (response: any) => {
        if (response && response.data && Array.isArray(response.data)) {
          this.bankAccounts = [
            { id: 0, active: true, accountName: 'Selecione uma conta bancária', initialBalance: 0, balanceDate: '' },
            ...response.data
          ];
        } else {
          console.warn('Resposta da API de contas bancárias não tem a estrutura esperada:', response);
          this.bankAccounts = [
            { id: 0, active: true, accountName: 'Selecione uma conta bancária', initialBalance: 0, balanceDate: '' }
          ];
        }
      },
      error: (error: any) => {
        console.error('Erro ao carregar contas bancárias:', error);
        this.bankAccounts = [
          { id: 0, active: true, accountName: 'Selecione uma conta bancária', initialBalance: 0, balanceDate: '' }
        ];
      }
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        if (control) {
          control.markAsTouched();
        }
      });
      return;
    }

    const formValue = this.form.value;
    
    const expenseDto: CreateExpenseDto = {
      date: formValue.dueDate,
      occurence: formValue.occurrence === 'Única' ? 1 : 2,
      description: formValue.description,
      installments: this.installments.length > 0 ? this.installments.length : 1,
      total: formValue.amount,
      typePaymentId: Number(formValue.paymentMethod),
      expenseId: Number(formValue.expenseType),
      expenseType: formValue.expenseTypeCategory,
      entityId: Number(formValue.customer),
      barCode: formValue.barcode,
      observations: formValue.notes || '',
      fee: formValue.interest,
      penalty: formValue.fine,
      bankAccountId: Number(formValue.bankAccount)
    };

    if (formValue.occurrence !== 'Única' && this.installments && this.installments.length > 0) {
      expenseDto.installmentsDetails = this.installments.map(inst => ({
        dueDate: inst.date,
        amount: inst.amount,
        status: inst.status === 'quitado' ? 'paid' : '',
        paymentMethod: Number(inst.paymentMethod)
      }));
    }

    this.financialService.createExpense(expenseDto).subscribe({
      next: () => {
        this.router.navigate(['/app/financial/expenses']);
      },
      error: (error: any) => {
        console.error('Error creating expense:', error);
      }
    });
  }



  goBack(): void {
    this.router.navigate(['/app/financial/expenses']);
  }

  openParcelModal() {
    if (!this.form.get('firstInstallmentDate')?.value) {
      this.form.get('firstInstallmentDate')?.setValue(this.form.get('dueDate')?.value);
    }
    this.showParcelModal = true;
  }
}
